import { AdSlotDataValues, AdSlotIdDataInput } from '@mbrtargeting/metatag-shared-types/metatag-core';

/**
 * Refine the search or IDs.
 */
interface AdSlotIdDataSearchOptions {
    /** show only ids from a given source */
    source?: string;
    /**
     * Show ids from a specific impression number (1-based), or -1 for all impressions.
     * If not specified, defaults to latest impression.
     */
    impression?: number;
}

/**
 * Internal data structure to store the IDs
 */
type AdSlotIdData = AdSlotIdDataInput & {
    timestamp: number;
    impression: number;
};

/**
 * Internal storage to save the IDs per slotname.
 */
const slotIdStore: Record<string, AdSlotIdData[]> = {};

/**
 * Internal storage to track current impression counter per slotname.
 */
const impressionCounter: Record<string, number> = {};

/**
 * Starts a new impression for a given slot. This should be called before reloading/refreshing an ad
 * to ensure IDs from different impressions don't get merged together.
 *
 * @param slotName the slotName to start a new impression for
 */
export const startNewImpression = (slotName: string) => {
    // initialize to 1 if not present, otherwise increment
    impressionCounter[slotName] = (impressionCounter[slotName] || 0) + 1;
};

/**
 * Stores ids for a given slot.
 *
 * @param slotName the slotName to store ids for
 * @param data source and values to store
 */
export const setIdsForAdSlot = (slotName: string, data: AdSlotIdDataInput) => {
    // create record for adslot if it does not exist and save data into array
    (slotIdStore[slotName] ||= []).push({
        ...data,
        // note: timestamp can be exactly the same when function is called too fast
        timestamp: Date.now(),
        // use current impression counter
        impression: impressionCounter[slotName],
    });
};

/**
 * Search for all ids on a given slot.
 *
 * @param slotName the slotName to search ids for
 * @param options Options allow to further refine the search, either by only considering IDs from a given source and/or by searching for ids that entered the system first (instead of last)
 */
export const getIdsForAdSlot = (slotName: string, options?: AdSlotIdDataSearchOptions): Partial<AdSlotDataValues> => {
    const { source = undefined, impression = impressionCounter[slotName] } = options || {};

    return (slotIdStore[slotName] || [])
        // filter by impression if specified
        .filter(data => !impression || data.impression === impression)
        // filter by source if one is given or just pass through otherwise
        .filter(data => !source || data.source === source)
        // merge all values into an result object after all records were sorted
        .reduce((result, data) => ({ ...result, ...data.values }), {});
};
